import React, { useEffect, useState } from 'react';
import { ConfigProvider, DatePicker, Space } from 'antd';
import type { DatePickerProps, RangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import weekday from 'dayjs/plugin/weekday';
import {
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';

const { RangePicker } = DatePicker;

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(weekday);

type PickerType = 'date' | 'week' | 'month' | 'quarter' | 'year';

interface SharedDatePickerProps {
  mode?: PickerType;
  isRange?: boolean;
  showTime?: boolean;
  onChange?: (value: any, dateString: string | [string, string]) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>, info: any) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>, info: any) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string | [string, string];
  startDate?: Date;
  endDate?: Date;
  date?: Date;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
  disableFutureDates?: boolean;
  size?: 'small' | 'middle' | 'large';
  dateFormat?: string;
  excludeDateIntervals?: { start: Date; end: Date }[];
  style?: React.CSSProperties;
  min?: Date;
  max?: Date;
  configDate?: Date;
  showWeek?: boolean;
  isWorkWeek?: boolean;
}

const SharedDatePicker: React.FC<SharedDatePickerProps> = ({
  mode = 'date',
  isRange = false,
  showTime = false,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  placeholder,
  startDate,
  endDate,
  date,
  disabled = false,
  allowClear = true,
  className,
  disableFutureDates,
  size = 'middle',
  dateFormat = 'MM/DD/YYYY',
  excludeDateIntervals = [],
  style,
  min,
  max,
  configDate,
  showWeek,
  isWorkWeek = false,
}) => {
  const start = startDate ? dayjs(startDate) : null;
  const end = endDate ? dayjs(endDate) : null;
  const SingleDate = date ? dayjs(date) : null;
  const [selectedDate, setSelectedDate] = useState<Dayjs>(
    dayjs(date) || dayjs(),
  );
  const [hoveredDate, setHoveredDate] = useState<Dayjs | null>(null);

  useEffect(() => {
    if (date) {
      setSelectedDate(dayjs(date));
    }
  }, [date]);

  const rangeValue: [Dayjs, Dayjs] | null = start && end ? [start, end] : null;

  const minDate = min ? dayjs(min) : null;
  const maxDate = max ? dayjs(max) : null;

  const disabledDate = (current: Dayjs) => {
    if (!current) return false;

    if (disableFutureDates && current.isAfter(dayjs(), 'day')) return true;
    if (minDate && current.isBefore(minDate, 'day')) return true;
    if (maxDate && current.isAfter(maxDate, 'day')) return true;
    if (configDate && current.isBefore(dayjs(configDate), 'day')) return true;

    return excludeDateIntervals.some(
      ({ start, end }) =>
        current.isSameOrAfter(dayjs(start), 'day') &&
        current.isSameOrBefore(dayjs(end), 'day'),
    );
  };

  const handleChange = (value: Dayjs | null, dateString: string) => {
    if (value) {
      setSelectedDate(value);
    }
    onChange?.(value, dateString);
  };

  const goToPreviousWeek = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newDate = selectedDate.subtract(1, 'week');

    setSelectedDate(newDate);
    onChange?.(newDate, newDate.format(dateFormat));
  };

  const goToNextWeek = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newDate = selectedDate.add(1, 'week');

    setSelectedDate(newDate);
    onChange?.(newDate, newDate.format(dateFormat));
  };

  const formatWeekDisplay = (value: Dayjs) => {
    const start = isWorkWeek
      ? value.startOf('week').add(1, 'day')
      : value.startOf('week');
    const end = isWorkWeek
      ? value.startOf('week').add(5, 'day')
      : value.endOf('week');

    return `${start.format('MM/DD/YYYY')} - ${end.format('MM/DD/YYYY')}`;
  };

  return (
    <div>
      {isRange ? (
        <RangePicker
          className="custom-bordered-input"
          style={style}
          picker={mode}
          showTime={showTime}
          onChange={onChange as RangePickerProps['onChange']}
          onFocus={onFocus}
          onBlur={onBlur}
          value={rangeValue || undefined}
          placeholder={
            placeholder
              ? (placeholder as [string, string])
              : ['Start date', 'End date']
          }
          disabled={disabled}
          disabledDate={disabledDate}
          onKeyDown={onKeyDown}
          size={size}
          format={dateFormat}
          inputReadOnly
        />
      ) : mode === 'week' ? (
        <DatePicker
          className="custom-bordered-input"
          onChange={handleChange}
          showWeek={showWeek}
          value={selectedDate}
          placeholder={placeholder as string}
          allowClear={allowClear && !date}
          disabledDate={disabledDate}
          size={size}
          format={(value) => formatWeekDisplay(value)}
          prefix={<CalendarOutlined style={{ fontSize: '16px' }} />}
          suffixIcon={
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                pointerEvents: 'auto',
              }}
            >
              <LeftOutlined
                onClick={goToPreviousWeek}
                style={{ cursor: 'pointer', color: '#000000' }}
              />
              <RightOutlined
                onClick={goToNextWeek}
                style={{ cursor: 'pointer', color: '#000000' }}
              />
            </span>
          }
          dateRender={(current) => {
            const hoveredStart = hoveredDate?.startOf('week');
            const hoveredEnd = hoveredDate?.endOf('week');

            const selectedStart = selectedDate?.startOf('week');
            const selectedEnd = selectedDate?.endOf('week');

            const isWeekday = current.day() >= 1 && current.day() <= 5;

            const getStartEnd = (date: Dayjs | null) => {
              if (!date) return [null, null];

              return isWorkWeek
                ? [
                    date.startOf('week').add(1, 'day'),
                    date.startOf('week').add(5, 'day'),
                  ]
                : [date.startOf('week'), date.endOf('week')];
            };

            const [hoverStart, hoverEnd] = getStartEnd(hoveredDate);
            const [selectStart, selectEnd] = getStartEnd(selectedDate);

            const inHoveredRange =
              hoveredDate &&
              current.isSameOrAfter(hoverStart, 'day') &&
              current.isSameOrBefore(hoverEnd, 'day');

            const inSelectedRange =
              selectedDate &&
              current.isSameOrAfter(selectStart, 'day') &&
              current.isSameOrBefore(selectEnd, 'day');

            const shouldHighlight = isWorkWeek
              ? (inHoveredRange || inSelectedRange) && isWeekday
              : inHoveredRange || inSelectedRange;

            const isStart =
              current.isSame(hoverStart, 'day') ||
              current.isSame(selectStart, 'day');
            const isEnd =
              current.isSame(hoverEnd, 'day') ||
              current.isSame(selectEnd, 'day');

            let classNames = shouldHighlight ? 'highlighted-cell' : '';

            if (shouldHighlight && isStart) classNames += ' start';
            if (shouldHighlight && isEnd) classNames += ' end';

            return (
              <div
                className={classNames}
                onMouseEnter={() => setHoveredDate(current)}
                onMouseLeave={() => setHoveredDate(null)}
              >
                {current.date()}
              </div>
            );
          }}
          showToday={false}
        />
      ) : (
        <DatePicker
          className="custom-bordered-input"
          popupClassName="custom-datepicker-popup"
          style={style}
          picker={mode}
          showTime={showTime}
          onChange={onChange as DatePickerProps['onChange']}
          onFocus={onFocus}
          onBlur={onBlur}
          value={SingleDate || undefined}
          placeholder={placeholder as string}
          disabled={disabled}
          disabledDate={disabledDate}
          onKeyDown={onKeyDown}
          showToday={false}
          size={size}
          format={dateFormat}
          inputReadOnly
        />
      )}
    </div>
  );
};

export default SharedDatePicker;
